import { api } from './api'
import {
  Consultation,
  ConsultationCreate,
  ConsultationUpdate,
  StandaloneProgressNoteCreate,
  ProgressNoteExt,
  ClarifyingNote,
  ClarifyingNoteCreate,
} from '../types'

export type { ProgressNoteExt }

export interface ConsultationSummary {
  id: string
  appointment_id: string
  doctor_id: string
  chief_complaint: string | null
  assessment: string | null
  plan_notes: string | null
  evolution_status?: 'improving' | 'stable' | 'worsening' | null
  visit_type?: string | null
  progress_note_ext?: ProgressNoteExt | null
  diagnosis_codes: string[]
  vitals?: Record<string, number | string> | null
  status: string
  completed_at: string | null
  created_at: string
  /** Signing author (NOM-004 REQ-NOTE-02); null on unsigned drafts. */
  attested_by_name?: string | null
  attested_by_cedula?: string | null
}

/** Backend caps `limit` at 100 per page (consultations.py). */
const PAGE_SIZE = 100

export const getConsultationsByPatient = async (
  patientId: string,
  limit = PAGE_SIZE,
  skip = 0,
): Promise<ConsultationSummary[]> => {
  return api.get<ConsultationSummary[]>(
    `/consultations/by-patient/${patientId}?skip=${skip}&limit=${limit}`,
  )
}

/**
 * Fetch EVERY consultation for a patient by paging through the capped endpoint.
 * Guarantees the evolution view never silently hides notes (REQ-FE-01). The
 * page guard (max 100 pages = 10k notes) is a runaway safety net, not a cap on
 * what a real patient record holds.
 */
export const getAllConsultationsByPatient = async (
  patientId: string,
): Promise<ConsultationSummary[]> => {
  const all: ConsultationSummary[] = []
  for (let page = 0; page < 100; page++) {
    const batch = await getConsultationsByPatient(patientId, PAGE_SIZE, page * PAGE_SIZE)
    all.push(...batch)
    if (batch.length < PAGE_SIZE) break
  }
  return all
}

export const getConsultationByAppointment = async (
  appointmentId: string,
): Promise<Consultation | null> => {
  try {
    return await api.get<Consultation>(`/consultations/by-appointment/${appointmentId}`)
  } catch {
    return null
  }
}

export const consultationService = {
  createConsultation: (data: ConsultationCreate): Promise<Consultation> => {
    return api.post<Consultation>('/consultations', data)
  },

  getConsultation: (consultationId: string): Promise<Consultation> => {
    return api.get<Consultation>(`/consultations/${consultationId}`)
  },

  updateConsultation: (consultationId: string, data: ConsultationUpdate): Promise<Consultation> => {
    return api.put<Consultation>(`/consultations/${consultationId}`, data)
  },

  completeConsultation: (consultationId: string): Promise<Consultation> => {
    return api.post<Consultation>(`/consultations/${consultationId}/complete`)
  },

  requestUnlock: (consultationId: string, reason: string): Promise<Consultation> => {
    return api.post<Consultation>(`/consultations/${consultationId}/request-unlock`, { reason })
  },

  approveUnlock: (consultationId: string): Promise<Consultation> => {
    return api.post<Consultation>(`/consultations/${consultationId}/approve-unlock`)
  },

  // A 404 here means the appointment simply has no consultation yet — an
  // expected state when linking an appointment to start a new note, not an
  // error. Resolve it to null so the editor shows the "Iniciar Consulta" flow
  // instead of an error; any other failure still propagates.
  getByAppointment: async (appointmentId: string): Promise<Consultation | null> => {
    try {
      return await api.get<Consultation>(`/consultations/by-appointment/${appointmentId}`)
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 404) {
        return null
      }
      throw err
    }
  },

  getByPatient: getConsultationsByPatient,

  createStandalone: (data: StandaloneProgressNoteCreate): Promise<Consultation> => {
    return api.post<Consultation>('/consultations/standalone', data)
  },

  // Clarifying notes (addenda) — REQ-INT-03
  listClarifyingNotes: (consultationId: string): Promise<ClarifyingNote[]> => {
    return api.get<ClarifyingNote[]>(`/consultations/${consultationId}/clarifying-notes`)
  },

  addClarifyingNote: (
    consultationId: string,
    data: ClarifyingNoteCreate,
  ): Promise<ClarifyingNote> => {
    return api.post<ClarifyingNote>(`/consultations/${consultationId}/clarifying-notes`, data)
  },
}
